import {
  PaymentAssignmentSortBy,
  PaymentAssignmentStatusId,
  PaymentFilters,
  PaymentType,
  PersonRelationshipEnum,
  Priority,
  TerminatedFilter,
  TestUserFilter,
} from './types'
import { PaymentTypeEnum } from 'types/benefit'

/**
 * URL query-param persistence for the Payment Assignment page.
 *
 * Keeps filters, sort, pagination and the open detail modal in the URL so a
 * user can copy the current URL and share the exact same view with someone
 * else (or reopen it later).
 */

export interface PaymentAssignmentUrlSort {
  sortBy: PaymentAssignmentSortBy
  sortOrder: 'asc' | 'desc'
}

export interface PaymentAssignmentUrlStateInput {
  filters: PaymentFilters
  sort: PaymentAssignmentUrlSort | null
  page: number
  pageSize: number
  detailCaseId: number | null
}

const PARAM = {
  period: 'period',
  search: 'q',
  carriers: 'carriers',
  types: 'types',
  priorities: 'priorities',
  statuses: 'statuses',
  clients: 'clients',
  assignees: 'assignees',
  escalators: 'escalators',
  submittedBy: 'submittedBy',
  relationships: 'relationships',
  testUser: 'testUser',
  terminated: 'terminated',
  binder: 'binder',
  onlyMigrated: 'onlyMigrated',
  effFrom: 'effFrom',
  effTo: 'effTo',
  endFrom: 'endFrom',
  endTo: 'endTo',
  sortBy: 'sortBy',
  sortOrder: 'sortOrder',
  page: 'page',
  pageSize: 'pageSize',
  detailCase: 'case',
  primaryPaymentMethods: 'primaryPaymentMethods',
} as const

const DEFAULT_PAGE_SIZE = 25

const VALID_PAYMENT_TYPES = new Set<string>(Object.values(PaymentType))
const VALID_PRIMARY_PAYMENT_METHODS = new Set<string>(
  Object.values(PaymentTypeEnum)
)
const VALID_STATUSES = new Set<string>(Object.values(PaymentAssignmentStatusId))
const VALID_RELATIONSHIPS = new Set<string>(
  Object.values(PersonRelationshipEnum)
)
const VALID_TEST_USER_FILTERS = new Set<string>(Object.values(TestUserFilter))
const VALID_TERMINATED_FILTERS = new Set<string>(
  Object.values(TerminatedFilter)
)
const VALID_PRIORITIES = new Set<number>(
  Object.values(Priority).filter((v): v is number => typeof v === 'number')
)
const VALID_SORT_BY = new Set<string>(Object.values(PaymentAssignmentSortBy))

function getSearchParams(): URLSearchParams {
  if (typeof window === 'undefined') return new URLSearchParams()
  return new URLSearchParams(window.location.search)
}

function splitCsv(value: string): string[] {
  return value
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v !== '')
}

function parseStringArray(
  params: URLSearchParams,
  key: string
): string[] | undefined {
  if (!params.has(key)) return undefined
  return splitCsv(params.get(key) ?? '')
}

function parseNumberArray(
  params: URLSearchParams,
  key: string
): number[] | undefined {
  if (!params.has(key)) return undefined
  return splitCsv(params.get(key) ?? '')
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v))
}

/**
 * Parse filter values from the URL. Only keys present in the URL are returned so
 * callers can merge the result over their defaults.
 */
export function parsePaymentAssignmentFiltersFromUrl(): Partial<PaymentFilters> {
  const params = getSearchParams()
  const out: Partial<PaymentFilters> = {}

  const period = params.get(PARAM.period)
  if (period) out.paymentPeriod = period

  const search = params.get(PARAM.search)
  if (search) out.search = search

  const carrierNames = parseStringArray(params, PARAM.carriers)
  if (carrierNames) out.carrierNames = carrierNames

  const paymentTypes = parseStringArray(params, PARAM.types)
  if (paymentTypes) {
    out.paymentTypes = paymentTypes.filter((t) =>
      VALID_PAYMENT_TYPES.has(t)
    ) as PaymentType[]
  }

  const priorityLevels = parseNumberArray(params, PARAM.priorities)
  if (priorityLevels) {
    out.priorityLevels = priorityLevels.filter((p) =>
      VALID_PRIORITIES.has(p)
    ) as Priority[]
  }

  const statuses = parseStringArray(params, PARAM.statuses)
  if (statuses) out.statuses = statuses.filter((s) => VALID_STATUSES.has(s))

  const clientPublicKeys = parseStringArray(params, PARAM.clients)
  if (clientPublicKeys) out.clientPublicKeys = clientPublicKeys

  const assigneeIds = parseNumberArray(params, PARAM.assignees)
  if (assigneeIds) out.assigneeIds = assigneeIds

  const escalatorIds = parseNumberArray(params, PARAM.escalators)
  if (escalatorIds) out.escalatorIds = escalatorIds

  const paymentSubmittedByIds = parseNumberArray(params, PARAM.submittedBy)
  if (paymentSubmittedByIds) out.paymentSubmittedByIds = paymentSubmittedByIds

  const relationships = parseStringArray(params, PARAM.relationships)
  if (relationships) {
    out.relationships = relationships.filter((r) =>
      VALID_RELATIONSHIPS.has(r)
    ) as PersonRelationshipEnum[]
  }

  const testUser = params.get(PARAM.testUser)
  if (testUser && VALID_TEST_USER_FILTERS.has(testUser)) {
    out.testUserFilter = testUser as TestUserFilter
  }

  const terminated = params.get(PARAM.terminated)
  if (terminated && VALID_TERMINATED_FILTERS.has(terminated)) {
    out.terminatedFilter = terminated as TerminatedFilter
  }

  const binder = params.get(PARAM.binder)
  if (binder === 'true') out.binderPayment = true
  else if (binder === 'false') out.binderPayment = false

  const primaryPaymentMethods = parseStringArray(
    params,
    PARAM.primaryPaymentMethods
  )
  if (primaryPaymentMethods) {
    out.primaryPaymentMethods = primaryPaymentMethods.filter((m) =>
      VALID_PRIMARY_PAYMENT_METHODS.has(m)
    )
  }
  const onlyMigrated = params.get(PARAM.onlyMigrated)
  if (onlyMigrated === 'true') out.onlyMigrated = true

  const effFrom = params.get(PARAM.effFrom)
  if (effFrom) out.benefitEffectiveDateFrom = effFrom
  const effTo = params.get(PARAM.effTo)
  if (effTo) out.benefitEffectiveDateTo = effTo
  const endFrom = params.get(PARAM.endFrom)
  if (endFrom) out.benefitEndDateFrom = endFrom
  const endTo = params.get(PARAM.endTo)
  if (endTo) out.benefitEndDateTo = endTo

  return out
}

export function parsePaymentAssignmentSortFromUrl(): PaymentAssignmentUrlSort | null {
  const params = getSearchParams()
  const sortBy = params.get(PARAM.sortBy)
  const sortOrder = params.get(PARAM.sortOrder)
  if (
    sortBy &&
    VALID_SORT_BY.has(sortBy) &&
    (sortOrder === 'asc' || sortOrder === 'desc')
  ) {
    return { sortBy: sortBy as PaymentAssignmentSortBy, sortOrder }
  }
  return null
}

export function parsePaymentAssignmentPaginationFromUrl(): {
  page?: number
  pageSize?: number
} {
  const params = getSearchParams()
  const pageRaw = Number(params.get(PARAM.page))
  const pageSizeRaw = Number(params.get(PARAM.pageSize))
  return {
    page:
      Number.isFinite(pageRaw) && pageRaw >= 1
        ? Math.floor(pageRaw)
        : undefined,
    pageSize:
      Number.isFinite(pageSizeRaw) && pageSizeRaw >= 1
        ? Math.floor(pageSizeRaw)
        : undefined,
  }
}

export function parsePaymentAssignmentDetailCaseIdFromUrl(): number | null {
  const params = getSearchParams()
  const raw = Number(params.get(PARAM.detailCase))
  return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : null
}

function setCsvParam(
  params: URLSearchParams,
  key: string,
  values?: Array<string | number | null | undefined>
) {
  if (!values || values.length === 0) return
  const cleaned = values
    .filter((v) => v !== null && v !== undefined && v !== '')
    .map((v) => String(v))
  if (cleaned.length === 0) return
  params.set(key, cleaned.join(','))
}

/** Serialize the current page state into a query string (without a leading '?'). */
export function buildPaymentAssignmentQueryString(
  state: PaymentAssignmentUrlStateInput
): string {
  const params = new URLSearchParams()
  const { filters: f, sort, page, pageSize, detailCaseId } = state

  if (f.paymentPeriod) params.set(PARAM.period, f.paymentPeriod)
  if (f.search && f.search.trim()) params.set(PARAM.search, f.search.trim())

  setCsvParam(params, PARAM.carriers, f.carrierNames)
  setCsvParam(params, PARAM.types, f.paymentTypes)
  setCsvParam(params, PARAM.priorities, f.priorityLevels)
  setCsvParam(params, PARAM.statuses, f.statuses)
  setCsvParam(params, PARAM.clients, f.clientPublicKeys)
  setCsvParam(params, PARAM.assignees, f.assigneeIds)
  setCsvParam(params, PARAM.escalators, f.escalatorIds)
  setCsvParam(params, PARAM.submittedBy, f.paymentSubmittedByIds)
  setCsvParam(params, PARAM.relationships, f.relationships)

  if (f.testUserFilter && f.testUserFilter !== TestUserFilter.ALL) {
    params.set(PARAM.testUser, f.testUserFilter)
  }
  if (f.terminatedFilter && f.terminatedFilter !== TerminatedFilter.ALL) {
    params.set(PARAM.terminated, f.terminatedFilter)
  }
  if (typeof f.binderPayment === 'boolean') {
    params.set(PARAM.binder, String(f.binderPayment))
  }
  if (f.onlyMigrated) {
    params.set(PARAM.onlyMigrated, 'true')
  }

  setCsvParam(params, PARAM.primaryPaymentMethods, f.primaryPaymentMethods)

  if (f.benefitEffectiveDateFrom)
    params.set(PARAM.effFrom, f.benefitEffectiveDateFrom)
  if (f.benefitEffectiveDateTo)
    params.set(PARAM.effTo, f.benefitEffectiveDateTo)
  if (f.benefitEndDateFrom) params.set(PARAM.endFrom, f.benefitEndDateFrom)
  if (f.benefitEndDateTo) params.set(PARAM.endTo, f.benefitEndDateTo)

  if (sort) {
    params.set(PARAM.sortBy, sort.sortBy)
    params.set(PARAM.sortOrder, sort.sortOrder)
  }

  if (page && page > 1) params.set(PARAM.page, String(page))
  if (pageSize && pageSize !== DEFAULT_PAGE_SIZE) {
    params.set(PARAM.pageSize, String(pageSize))
  }

  if (detailCaseId != null) params.set(PARAM.detailCase, String(detailCaseId))

  return params.toString()
}

/**
 * Replace the current URL (without adding a history entry) so it reflects the
 * given page state. No-op when the URL is already up to date.
 */
export function syncPaymentAssignmentUrl(
  state: PaymentAssignmentUrlStateInput
): void {
  if (typeof window === 'undefined') return
  const queryString = buildPaymentAssignmentQueryString(state)
  const newUrl = `${window.location.pathname}${
    queryString ? `?${queryString}` : ''
  }${window.location.hash}`
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`
  if (newUrl !== currentUrl) {
    window.history.replaceState(window.history.state, '', newUrl)
  }
}
